import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useContext } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ACCOUNT_TYPE, ACCOUNT_TYPE_LIST, isDebt } from '../constants/ACCOUNT_TYPE'
import { svc_getAccountsGroupedByType } from "../services/account.service";
import { getInternalOBCustomerAccounts, createOrUpdateAccounts, groupOBAccountsByType, generateAppToken } from "../utilities/ob-utils";
import { OBCustomerContext } from "../contexts/ob-customer-context";
import { svc_ob_refreshCustomerAccounts } from "../services/external-openbanking.service";

import AccountGroup from "../components/Account/AccountGroup";
import AccountsGetStarted from "../components/GetStarted/AccountsGetStarted";
import LoadingSpinner from '../components/ui/LoadingSpinner'
import { IconBudget, IconRefresh } from "../assets/icons";
import AccountIncludedTooltip from "../components/Account/AccountIncludedTooltip";
import { formatCurrency } from "../utilities/general-utils";

const Accounts = () => {
    const { getAccessTokenSilently, user } = useAuth0();
    const { obCustomer, setOBAppToken, updateOBCustomer } = useContext(OBCustomerContext);
    const [ searchParams ] = useSearchParams();
    const includedParam = searchParams.get('included');
    const connectParam = searchParams.get('connect');

    const [ netWorth, setNetWorth ] = useState(null);
    const [ obCustomerAccounts, setOBCustomerAccounts ] = useState(null);
    const [ groupedManualAccounts, setGroupedManualAccounts ] = useState(null);
    const [ combinedAccounts, setCombinedAccounts ] = useState(null);
    const [ isLoading, setIsLoading ] = useState(true);
    const [ hasIncludedAccounts, setHasIncludedAccounts ] = useState(false);
    const [ showIncludedAccounts, setShowIncludedAccounts ] = useState(includedParam);

    const getGroupedManualAccounts = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await svc_getAccountsGroupedByType(accessToken, user.sub);

        if (error) {
            console.error(error)
            setIsLoading(false)
            return
        }

        let groups = await data

        groups.forEach(group => {
            if (typeof group.accounts === 'string') {
                group.accounts = JSON.parse(group.accounts)
            }
            group.balance = getCombinedAccountsBalance(group.accounts)
        })

        groups.filter(group => group.accounts.some(account => account.includeBalance === 1)).length > 0 && setHasIncludedAccounts(true)

        setGroupedManualAccounts(groups)
    }

    const getOBCustomerAccounts = async () => {
        const accessToken = await getAccessTokenSilently();

        const onFailure = async (error) => {
            if (parseInt(error.statusCode) === 401) {
                const updatedToken = await generateAppToken(accessToken);
                setOBAppToken(updatedToken);
                updateOBCustomer(updatedToken);
                getInternalOBCustomerAccounts(obCustomer, accessToken, user.sub, setOBCustomerAccounts, alert('Failed to get accounts. Please try again.'));
                return;
            }
        }

        const obAccounts = await getInternalOBCustomerAccounts(
            obCustomer,
            accessToken,
            user.sub,
            setOBCustomerAccounts,
            onFailure
        )

        const groupedOBAccounts = groupOBAccountsByType(obAccounts);
        combineOBAndManualAccounts(groupedOBAccounts)
    }
    
    const combineOBAndManualAccounts = (obAccounts) => {
        const manualAccounts = groupedManualAccounts
        const accountsCombined = Object.keys(obAccounts).map(type => {
            const manualMatch = manualAccounts.find(group => group.type === type)?.accounts
            const obAccountsType = obAccounts[type];

            return {
                type,
                accounts: obAccountsType.concat(manualMatch || [])
            }
        }).concat(manualAccounts.filter(group => !obAccounts[group.type]))

        accountsCombined.forEach(group => {
            group.balance = getCombinedAccountsBalance(group.accounts)

            group.accounts.sort((a, b) => {
                const balancePropertyA = a.hasOwnProperty("obCustomerId")
                    ? a.data.balance
                    : isDebt(a.type) ? a.debtAccountInfo.balance : a.availableBalance

                const balancePropertyB = b.hasOwnProperty("obCustomerId")
                    ? b.data.balance
                    : isDebt(b.type) ? b.debtAccountInfo.balance : b.availableBalance

                return isDebt(a.type)
                    ? balancePropertyA - balancePropertyB
                    : balancePropertyB - balancePropertyA
            });
        })
        
        accountsCombined.sort((a, b) => {
            return ACCOUNT_TYPE_LIST.indexOf(a.type) - ACCOUNT_TYPE_LIST.indexOf(b.type)
        })

        setNetWorth(accountsCombined.reduce((acc, group) => acc + group.balance, 0));
        setCombinedAccounts(accountsCombined)
    }

    const handleRefreshConnectedAccounts = async (obToken = obCustomer.obToken) => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await svc_ob_refreshCustomerAccounts(accessToken, obToken, obCustomer.obId);

        setCombinedAccounts(null);
        setIsLoading(true);

        if (error) {
            if (parseInt(error.statusCode) === 401) {
                const updatedToken = await generateAppToken(accessToken);
                setOBAppToken(updatedToken);
                updateOBCustomer(updatedToken);
                handleRefreshConnectedAccounts(updatedToken);
                return;
            }
            console.error('error', error);
            return;
        }
        
        createOrUpdateAccounts(accessToken, data.accounts, obCustomer, user.sub).then(async() => {
            const refreshedAccounts = await getInternalOBCustomerAccounts(obCustomer, accessToken, user.sub);

            const groupedOBAccounts = groupOBAccountsByType(refreshedAccounts);
            combineOBAndManualAccounts(groupedOBAccounts)
            setOBCustomerAccounts(refreshedAccounts)
        });
    }

    const handleFilterChange = (e) => {
        setShowIncludedAccounts(e.target.checked)
    }

    useEffect(() => {
        if (combinedAccounts) {
            setIsLoading(false)
        }
        if (user && !groupedManualAccounts) {
            getGroupedManualAccounts();
        }
        if (obCustomer && groupedManualAccounts && !obCustomerAccounts) {
            getOBCustomerAccounts();
        }
    }, [user, obCustomer, groupedManualAccounts, obCustomerAccounts, combinedAccounts])

    return (
        <div>
            <h1 className="text-center">My Accounts</h1>

            <div style={{
                width: '100%',
                maxWidth: '800px',
                margin: '0 auto',
            }}>
                {isLoading && <LoadingSpinner />}

                {netWorth !== null && (
                    <div className="text-right spc-m-y-1">
                        <h4 className="dis-inline-block bg-white spc-m-0 spc-p-y-half spc-p-x-1 text-weight-400">
                            <span className="text-weight-300 text-transform-uppercase" style={{fontSize: '60%'}}>Net Worth:</span> {formatCurrency(netWorth)}
                        </h4>
                    </div>
                )}

                {combinedAccounts && combinedAccounts.length > 0 && (
                    <AccountsControls
                        handleRefreshConnectedAccounts={handleRefreshConnectedAccounts}
                        handleFilterChange={handleFilterChange}
                        showIncludedAccounts={showIncludedAccounts}
                        obCustomerAccounts={obCustomerAccounts}
                    />
                )}

                {!isLoading && combinedAccounts && (
                    combinedAccounts
                        .filter(group => {
                            if (hasIncludedAccounts && showIncludedAccounts) {
                                return group.accounts.some(account => account.includeBalance === 1)
                            }
                            return true
                        })
                        .map(group =>
                            <AccountGroup
                                showIncludedAccounts={showIncludedAccounts}
                                group={group}
                                title={groupTypeTitle(group.type)}
                                key={group.type}
                            />
                        )
                )}
            </div>

            <div style={{
                margin: '2rem auto 0',
                padding: '2rem 1rem',
            }}>
                <AccountsGetStarted launchConnect={connectParam} />
            </div>
        </div>
    )
}

const groupTypeTitle = (type) => {
    switch (type) {
        case ACCOUNT_TYPE.CHECKING:
            return 'Checking'
        case ACCOUNT_TYPE.SAVINGS:
            return 'Savings'
        case ACCOUNT_TYPE.CREDIT_CARD:
            return 'Credit Cards'
        case ACCOUNT_TYPE.LINE_OF_CREDIT:
            return 'Lines of Credit'
        case ACCOUNT_TYPE.LOAN:
            return 'Loans'
        case ACCOUNT_TYPE.INVESTMENT:
            return 'Investments'
        case ACCOUNT_TYPE.MORTGAGE:
            return 'Mortgages'
        case ACCOUNT_TYPE.STUDENT_LOAN:
            return 'Student Loans'
        case ACCOUNT_TYPE.OTHER:
        default:
            return 'Other Accounts'
    }
}

const getCombinedAccountsBalance = (accounts) => {
    if (accounts.length === 0 || !accounts.length) return 0

    let balance = 0

    if (accounts && accounts.length > 0) {
        accounts.forEach(account => {
            if (account.hasOwnProperty("obCustomerId")) {
                balance += Math.abs(account.data.balance);
            } else {
                balance += isDebt(account.type)
                    ? account.debtAccountInfo.balance
                    : account.availableBalance;
            }
        })
    }

    if (isDebt(accounts[0].type)) {
        balance *= -1
    }

    return balance
}

const AccountsControls = ({
    handleRefreshConnectedAccounts,
    handleFilterChange,
    showIncludedAccounts,
    obCustomerAccounts,
}) => {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            alignItems: 'center',
            margin: '1rem 0',
        }}>
            <div className="text-left">
                <div
                    className="formField formField-inline-checkbox spc-m-r-1 spc-m-b-half"
                >
                    <input
                        type="checkbox"
                        id="includedAccountsFilter"
                        defaultChecked={showIncludedAccounts}
                        onChange={handleFilterChange}
                        style={{marginBottom: 0}}
                    />
                    <label htmlFor="includedAccountsFilter">
                        <span style={{display: 'inline-block', marginRight: '.5rem', fontSize: '1.2rem'}}><IconBudget /></span>
                        <span>Show accounts included in budget</span>
                    </label>
                    <AccountIncludedTooltip />
                </div>
            </div>

            {obCustomerAccounts && obCustomerAccounts.length > 0 && (
                <button
                    className="button button-blue button-slim spc-m-b-half"
                    onClick={handleRefreshConnectedAccounts}
                >
                <IconRefresh fill="#fff" /> Refresh Connected Accounts
            </button>
            )}
        </div>
    )
}

export default Accounts